/* eslint-disable no-underscore-dangle */
import {
  CheckoutDeliveryOption,
  CheckoutErrorFragment,
  CheckoutFragment,
  CheckoutState,
  CheckoutUnprocessableErrorsFragment,
  CollectionsV2Fragment,
  CollectionsQueryErrorResultFragment,
  Currency,
  DeliveryMinervaErrorsFragment,
  DeliveryOption,
  DiscountType,
  PaymentIntentErrorResultFragment,
  ProductFragment,
  ProductQueryErrorResultFragment,
  ProductsFragment,
  ProductsQueryErrorResultFragment,
  StoreDataFragment,
  StorePromoCodeFixedValueFragment,
  StorePromoCodeFreeShippingFragment,
  StorePromoCodePercentageFragment,
  StoreQueryErrorResultFragment,
  ProductBlankQueryErrorResultFragment,
  InitiateExpressPaymentResponseFragment,
  CheckoutUnprocessableErrorFragment,
  StoreThemeNewsletterSignupStorefrontFragment,
  StoreThemeExtendedPrimaryColorImmediateStorefrontFragment,
  StoreThemeExtendedPrimaryColorIndirectStorefrontFragment,
} from '@/gql';
import { safeUnreachable } from '@/lib/tsutils';

/**
 * Type guard for CheckoutDeliveryOption
 *
 * @param candidate The maybe a CheckoutDeliveryOption to test
 * @returns true if the candidate appears to be a CheckoutDeliveryOption
 */
export function isCheckoutDeliveryOption(candidate: any): candidate is CheckoutDeliveryOption {
  const checkoutDeliveryOption: CheckoutDeliveryOption = candidate as CheckoutDeliveryOption;

  switch (checkoutDeliveryOption) {
    case 'fullRushCost':
    case 'fullStandardCost':
    case 'fullSuperRushCost':
      return true;
    default:
      // This wont compile if the above case statements aren't exhaustive for CheckoutDeliveryOption, but
      // will return false if reached (and candidate isn't a CheckoutDeliveryOption)
      return safeUnreachable(checkoutDeliveryOption, false);
  }
}

/**
 * Type guard for CheckoutState
 *
 * @param candidate The maybe a CheckoutState to test
 * @returns true if the candidate appears to be a CheckoutState
 */
export function isCheckoutState(candidate: any): candidate is CheckoutState {
  const checkoutState: CheckoutState = candidate as CheckoutState;

  switch (checkoutState) {
    case 'completed':
    case 'created':
    case 'failure':
    case 'pending':
    case 'success':
      return true;
    default:
      // This wont compile if the above case statements aren't exhaustive for CheckoutState, but
      // will return false if reached (and candidate isn't a CheckoutState)
      return safeUnreachable(checkoutState, false);
  }
}

/**
 * Type guard for Currency
 *
 * @param candidate The maybe a Currency to test
 * @returns true if the candidate appears to be a Currency
 */
export function isCurrency(candidate: any): candidate is Currency {
  const currency: Currency = candidate as Currency;

  switch (currency) {
    case 'AUD':
    case 'CAD':
    case 'EUR':
    case 'GBP':
    case 'USD':
      return true;
    default:
      // This wont compile if the above case statements aren't exhaustive for Currency, but
      // will return false if reached (and candidate isn't a Currency)
      return safeUnreachable(currency as never, false);
  }
}

/**
 * Type guard for DeliveryOption
 *
 * @param candidate The maybe a DeliveryOption to test
 * @returns true if the candidate appears to be a DeliveryOption
 */
export function isDeliveryOption(candidate: any): candidate is DeliveryOption {
  const deliveryOption: DeliveryOption = candidate as DeliveryOption;

  switch (deliveryOption) {
    case 'standard':
    case 'rush':
    case 'super_rush':
      return true;
    default:
      // This wont compile if the above case statements aren't exhaustive for DeliveryOption, but
      // will return false if reached (and candidate isn't a DeliveryOption)
      return safeUnreachable(deliveryOption, false);
  }
}

/**
 * Type guard for DiscountType
 *
 * @param candidate The maybe a DiscountType to test
 * @returns true if the candidate appears to be a DiscountType
 */
export function isDiscountType(candidate: any): candidate is DiscountType {
  const discountType: DiscountType = candidate as DiscountType;

  switch (discountType) {
    case 'fixed_value':
    case 'free_shipping':
    case 'percentage':
      return true;
    default:
      // This wont compile if the above case statements aren't exhaustive for DiscountType, but
      // will return false if reached (and candidate isn't a DiscountType)
      return safeUnreachable(discountType, false);
  }
}

/** Simple type guard generator for any Fragment type
 *
 * @param typename
 * @returns (candidate: Maybe<T>) => candidate is T - Type guard to checkout a candidate against a fragment type
 */
export const makeFragmentTypeGuard =
  <T extends { __typename?: string }>(typename: string) =>
  (candidate: any): candidate is T => {
    const fragment = candidate as T;
    return Boolean(fragment && typeof fragment === 'object' && fragment?.__typename === typename);
  };

export const isProductFragment = makeFragmentTypeGuard<ProductFragment>('Product');
export const isProductsFragment = makeFragmentTypeGuard<ProductsFragment>('Products');
export const isProductQueryErrorResultFragment =
  makeFragmentTypeGuard<ProductQueryErrorResultFragment>('ProductQueryErrorResult');
export const isProductsQueryErrorResultFragment =
  makeFragmentTypeGuard<ProductsQueryErrorResultFragment>('ProductsQueryErrorResult');
export const isStoreDataFragment = makeFragmentTypeGuard<StoreDataFragment>('Store');
export const isStoreQueryErrorResultFragment =
  makeFragmentTypeGuard<StoreQueryErrorResultFragment>('StoreQueryErrorResult');
export const isStorePromoCodePercentageFragment =
  makeFragmentTypeGuard<StorePromoCodePercentageFragment>('PromoCodePercentage');
export const isStorePromoCodeFreeShippingFragment =
  makeFragmentTypeGuard<StorePromoCodeFreeShippingFragment>('PromoCodeFreeShipping');
export const isStorePromoCodeFixedValueFragment =
  makeFragmentTypeGuard<StorePromoCodeFixedValueFragment>('PromoCodeFixedValue');
export const isCollectionsV2Fragment = makeFragmentTypeGuard<CollectionsV2Fragment>('CollectionsV2');
export const isCollectionsQueryErrorResultFragment =
  makeFragmentTypeGuard<CollectionsQueryErrorResultFragment>('CollectionsQueryErrorResult');
export const isCheckoutFragment = makeFragmentTypeGuard<CheckoutFragment>('Checkout');
export const isInitiateExpressPaymentResponseFragment = makeFragmentTypeGuard<InitiateExpressPaymentResponseFragment>(
  'InitiateExpressPaymentResponse'
);
export const isPaymentIntentErrorResultFragment =
  makeFragmentTypeGuard<PaymentIntentErrorResultFragment>('PaymentIntentErrorResult');
export const isCheckoutErrorFragment = makeFragmentTypeGuard<CheckoutErrorFragment>('CheckoutError');
export const isDeliveryMinervaErrorsFragment =
  makeFragmentTypeGuard<DeliveryMinervaErrorsFragment>('DeliveryMinervaErrors');
export const isCheckoutUnprocessableErrorsFragment =
  makeFragmentTypeGuard<CheckoutUnprocessableErrorsFragment>('CheckoutUnprocessableErrors');
export const isCheckoutUnprocessableErrorFragment =
  makeFragmentTypeGuard<CheckoutUnprocessableErrorFragment>('CheckoutUnprocessableError');
export const isProductBlankQueryErrorResult =
  makeFragmentTypeGuard<ProductBlankQueryErrorResultFragment>('ProductBlankQueryErrorResult');
export const isStoreThemeNewsletterSignupStorefrontFragment =
  makeFragmentTypeGuard<StoreThemeNewsletterSignupStorefrontFragment>('NewsletterSignupStorefront');
export const isStoreThemeExtendedPrimaryColorImmediateStorefrontFragment =
  makeFragmentTypeGuard<StoreThemeExtendedPrimaryColorImmediateStorefrontFragment>(
    'ExtendedPrimaryColorImmediateStorefront'
  );
export const isStoreThemeExtendedPrimaryColorIndirectStorefrontFragment =
  makeFragmentTypeGuard<StoreThemeExtendedPrimaryColorIndirectStorefrontFragment>(
    'ExtendedPrimaryColorIndirectStorefront'
  );
